/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Menu, Button, MenuItem } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import styled, { css } from 'styled-components';
import StyledProps from '../common/theme/AppThemeInterface';
import { useEffect } from 'react';
import isNil from 'lodash/isNil';
export interface DropDownProps {
  // The text to display when nothing has been selected yet
  placeholder?: string;

  // The list of items to display in the dropdown
  items: DropDownItem[];

  // The initial key
  initialKey?: any;

  // The width in pixels of the dropdown
  width: string;

  // The handler when an item is selected. The selected item is emitted
  onItemSelected: (selectedItem: DropDownItem) => void;

  // If disabled is true, the dropdown does not open and is muted
  disabled?: boolean;
}

// The key to be displayed in the dropdown. The label is what is displayed in the list and in the button upon
// selection. The key is the key of the item. Upon selection the selected item is emitted as a DropDownItem
export interface DropDownItem {
  label: string;
  key: string | number | boolean;
}

interface SelectWrapperProps {
  width: string;
}

const DropDown = (props: DropDownProps) => {
  const { items, placeholder, onItemSelected, width, disabled } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = React.useState<null | DropDownItem>(null);

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    if (!props.disabled) {
      setAnchorEl(event.currentTarget);
    }
  }
  function setCurrentItem(event: DropDownItem) {
    setSelectedItem(event);
    onItemSelected(event);
    setAnchorEl(null);
  }
  function handleClose() {
    setAnchorEl(null);
  }

  const open = Boolean(anchorEl);

  // What to display in the button. If there is a selected item show the label, else
  // if there isn't an item selected yet use the placeholder text. If the placeholder
  // text has not been defined use an empty string
  const buttonText = (selectedItem: any, placeholder: string | undefined) => {
    let text = '';
    if (!isNil(selectedItem)) {
      text = selectedItem.label;
    } else if (!isNil(placeholder)) {
      text = placeholder;
    }
    return text;
  };

  useEffect(() => {
    const { initialKey, items } = props;

    if (!isNil(initialKey)) {
      const itemFromKey = items.filter((dropDownItem: DropDownItem) => {
        return dropDownItem.key === initialKey;
      });
      if (!isNil(itemFromKey) && itemFromKey.length) {
        setSelectedItem(itemFromKey[0]);
      }
    }
  });

  return (
    <SelectWrapper data-test="select-menu" className="" width={width}>
      <div>
        <StyledButton
          variant="text"
          width={width}
          onClick={handleClick}
          disabled={disabled}
          classes={{ root: 'dropdown__button-enabled', disabled: 'dropdown__button-disabled', label: 'label' }}
        >
          <span className="placeholder__text">{buttonText(selectedItem, placeholder)}</span>
          {open ? <DropDownButtonIconUp disabled={disabled} /> : <DropDownButtonIconDown disabled={disabled} />}
        </StyledButton>
        <StyledMenu
          className="select__menu"
          open={open}
          width={width}
          anchorEl={anchorEl}
          onClose={handleClose}
          classes={{ paper: 'paper' }}
          elevation={0}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {items.map((item: DropDownItem) => (
            <StyledMenuItem
              data-test={`select-menu-${item.key}`}
              key={item.key}
              onClick={() => {
                setCurrentItem(item);
              }}
              value={item.key}
            >
              {item.label}
            </StyledMenuItem>
          ))}
        </StyledMenu>
      </div>
    </SelectWrapper>
  );
};
export default DropDown;
interface DropDownButtonIconProps {
  disabled: boolean;
}
const DropDownButtonIconDown = styled(KeyboardArrowDown)`
  position: absolute;
  right: 0px;
  ${({ disabled }: DropDownButtonIconProps) => css`
    color: ${disabled
      ? ({ theme }: StyledProps) => theme.disabledTextColor
      : ({ theme }: StyledProps) => theme.primaryButtonTextColor};
  `};
`;

const DropDownButtonIconUp = styled(KeyboardArrowUp)`
  position: absolute;
  right: 0px;
  ${({ disabled }: DropDownButtonIconProps) => css`
    color: ${disabled
      ? ({ theme }: StyledProps) => theme.disabledTextColor
      : ({ theme }: StyledProps) => theme.primaryButtonTextColor};
  `};
`;
interface SelectWrapperProps {
  width: string;
}

const SelectWrapper = styled.div`
  ${({ width }: SelectWrapperProps) => css`
    width: ${width};
  `};
  .placeholder__text {
    position: absolute;
    left: 14px;
    font-weight: normal;
    font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  }
  .dropdown__toggle_button {
    width: 194px !important;
    min-width: 194px !important;
    max-width: 194px !important;
  }
  .dropdown__button-enabled {
    color: ${({ theme }: StyledProps) => theme.primaryButtonTextColor};
  }
  .dropdown__button-disabled {
    color: ${({ theme }: StyledProps) => theme.disabledTextColor};
  }
`;

interface StyledButtonProps {
  width: string;
  disabled: boolean;
}
const StyledButton = styled(Button)`
  ${({ width }: StyledButtonProps) => css`
    width: ${width};
    min-width: ${width};
    max-width: ${width};
  `};
  text-transform: none;
  height: 36px;
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  padding: 0;
  font-size: 14px;
  line-height: 0;
  background-color: ${({ theme }: StyledProps) => theme.dropdownBackgroundColor};
  border: none;
  text-align: left;

  .label {
    justify-content: flex-end;
  }
`;

interface StyledMenuProps {
  width: string;
}

const StyledMenu = styled(Menu)`
  ${({ width }: StyledMenuProps) => css`
    width: ${width};
    min-width: ${width};
    max-width: ${width};
  `};
  .paper {
    ${({ width }: StyledMenuProps) => css`
      width: ${width};
      min-width: ${width};
      max-width: ${width};
    `};
    margin-top: 1px;
    background-color: ${({ theme }: StyledProps) => theme.dropdownBackgroundColor};
    padding: 0;
    border: none;
    border-radius: 4px;
    box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12),
      0 5px 5px -3px rgba(0, 0, 0, 0.2);
  }
`;
interface StyledMenuItemProps {
  width: string;
}
const StyledMenuItem = styled(MenuItem)`
  background-color: ${({ theme }: StyledProps) => theme.dropdownBackgroundColor};
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  text-transform: none;
  height: 36px;
  ${({ width }: StyledMenuItemProps) => css`
    width: ${width};
    min-width: ${width};
    max-width: ${width};
  `};
  padding: 0 0 0 11px;
  font-size: 14px;
  line-height: 36px;
  color: white;
  &:hover {
    background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor} !important;
  }
  &:focus {
    background-color: ${({ theme }: StyledProps) => theme.dropdownBackgroundColor};
    & .MuiListItemIcon-root,
    & .MuiListItemText-primary {
      color: white;
    }
  }
`;
